import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, Github } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_1f988948-8d96-469b-9503-c5534672fbb7/artifacts/ygf96dzb_juiceapp%20logo.png";
const APP_STORE_URL = "https://apps.apple.com/fr/app/juiceapp-cold-press-ai/id1166117781?l=en-GB";
const GITHUB_URL = "https://github.com/mathieuSun/JUICEAPPWEB.git";

const categories = [
  { slug: 'juice-recipes', name: { en: 'Juice Recipes', fr: 'Recettes de Jus', es: 'Recetas de Jugos' } },
  { slug: 'health-benefits', name: { en: 'Health Benefits', fr: 'Bienfaits Santé', es: 'Beneficios para la Salud' } },
  { slug: 'detox', name: { en: 'Detox & Cleansing', fr: 'Détox et Nettoyage', es: 'Desintoxicación' } },
  { slug: 'nutrition-science', name: { en: 'Nutrition Science', fr: 'Science Nutritionnelle', es: 'Ciencia Nutricional' } },
  { slug: 'lifestyle', name: { en: 'Juice Lifestyle', fr: 'Style de Vie', es: 'Estilo de Vida' } }
];

export const Footer = () => {
  const { language, t } = useLanguage();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#f1f5f9] border-t border-[#e2e8f0]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img src={LOGO_URL} alt="JuiceApp" className="h-10 w-10" />
              <span className="font-heading text-xl font-semibold tracking-tight text-[#0f172a]">
                JuiceApp
              </span>
            </div>
            <p className="text-sm text-[#475569] mb-6 leading-relaxed">
              {t('footer.tagline')}
            </p>
            <a 
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#16a34a] text-white rounded-full text-sm font-medium hover:bg-[#15803d] transition-colors"
              data-testid="footer-app-store"
            >
              Download App
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>

          {/* Categories */}
          <div>
            <h4 className="font-heading text-lg font-normal text-[#0f172a] mb-4">
              {t('footer.categories')}
            </h4>
            <ul className="space-y-2">
              {categories.map((cat) => (
                <li key={cat.slug}>
                  <Link 
                    to={`/blog?category=${cat.slug}`}
                    className="text-sm text-[#475569] hover:text-[#16a34a] transition-colors"
                    data-testid={`footer-cat-${cat.slug}`}
                  >
                    {cat.name[language]}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-heading text-lg font-normal text-[#0f172a] mb-4">
              {t('footer.company')}
            </h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm text-[#475569] hover:text-[#16a34a] transition-colors">
                  {t('nav.home')}
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-sm text-[#475569] hover:text-[#16a34a] transition-colors">
                  {t('nav.blog')}
                </Link>
              </li>
              <li>
                <a 
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#475569] hover:text-[#16a34a] transition-colors"
                >
                  {t('nav.download')}
                </a>
              </li>
              <li>
                <a 
                  href={GITHUB_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#475569] hover:text-[#16a34a] transition-colors flex items-center gap-2"
                >
                  <Github className="h-4 w-4" />
                  GitHub
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="font-heading text-lg font-normal text-[#0f172a] mb-4">
              {t('footer.legal')}
            </h4>
            <ul className="space-y-2">
              <li>
                <a href="#privacy" className="text-sm text-[#475569] hover:text-[#16a34a] transition-colors">
                  {t('footer.privacy')}
                </a>
              </li>
              <li>
                <a href="#terms" className="text-sm text-[#475569] hover:text-[#16a34a] transition-colors">
                  {t('footer.terms')}
                </a>
              </li>
            </ul>
            
            {/* Resources - External Authority Links */}
            <h4 className="font-heading text-lg font-normal text-[#0f172a] mt-6 mb-4">
              Resources
            </h4>
            <ul className="space-y-2">
              <li>
                <a 
                  href="https://www.nih.gov/health-information"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#475569] hover:text-[#16a34a] transition-colors"
                >
                  NIH Health Info
                </a>
              </li>
              <li>
                <a 
                  href="https://www.nutrition.gov"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#475569] hover:text-[#16a34a] transition-colors"
                >
                  Nutrition.gov
                </a>
              </li>
              <li>
                <a 
                  href="https://pubmed.ncbi.nlm.nih.gov"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#475569] hover:text-[#16a34a] transition-colors"
                >
                  PubMed Research
                </a>
              </li>
              <li>
                <a 
                  href="https://www.who.int/health-topics/nutrition"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[#475569] hover:text-[#16a34a] transition-colors"
                >
                  WHO Nutrition
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Our Network — sibling brands, same owner. Boosts entity authority + cross-domain trust. */}
        <div className="border-t border-[#e2e8f0] mt-12 pt-8">
          <h4 className="font-heading text-sm font-medium uppercase tracking-widest text-[#16a34a] mb-4 text-center">
            Our Network
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <a
              href="https://allinya.app"
              target="_blank"
              rel="noopener"
              className="group border border-[#e2e8f0] rounded-xl p-4 hover:border-[#16a34a]/40 transition-colors text-center"
              data-testid="network-allinya"
              title="Allinya — All-in-one wellness app"
            >
              <p className="font-heading text-base font-medium text-[#0f172a] group-hover:text-[#16a34a] transition-colors">Allinya</p>
              <p className="text-xs text-[#475569] mt-1">All-in-one wellness</p>
            </a>
            <a
              href="https://zenreviewz.app"
              target="_blank"
              rel="noopener"
              className="group border border-[#e2e8f0] rounded-xl p-4 hover:border-[#16a34a]/40 transition-colors text-center"
              data-testid="network-zenreviewz"
              title="ZenReviewz — Honest wellness product reviews"
            >
              <p className="font-heading text-base font-medium text-[#0f172a] group-hover:text-[#16a34a] transition-colors">ZenReviewz</p>
              <p className="text-xs text-[#475569] mt-1">Wellness reviews</p>
            </a>
            <a
              href="https://healcandida.org"
              target="_blank"
              rel="noopener"
              className="group border border-[#e2e8f0] rounded-xl p-4 hover:border-[#16a34a]/40 transition-colors text-center"
              data-testid="network-healcandida"
              title="HealCandida — Natural candida healing protocols"
            >
              <p className="font-heading text-base font-medium text-[#0f172a] group-hover:text-[#16a34a] transition-colors">HealCandida</p>
              <p className="text-xs text-[#475569] mt-1">Candida healing</p>
            </a>
            <a
              href="https://gutzen.org"
              target="_blank"
              rel="noopener"
              className="group border border-[#e2e8f0] rounded-xl p-4 hover:border-[#16a34a]/40 transition-colors text-center"
              data-testid="network-gutzen"
              title="GutZen — Gut health protocols and science"
            >
              <p className="font-heading text-base font-medium text-[#0f172a] group-hover:text-[#16a34a] transition-colors">GutZen</p>
              <p className="text-xs text-[#475569] mt-1">Gut health science</p>
            </a>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-[#e2e8f0] pt-8 text-center">
          <p className="text-xs text-[#475569]">
            &copy; {currentYear} JuiceApp. {t('footer.rights')}.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
